import React from 'react'

import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  AvatarSize,
} from '@/components/ui/avatar'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import { CompanyEntity } from '@/types/entities'

interface Props {
  company: Pick<CompanyEntity, 'presentationName' | 'iconUrl'>
  size?: AvatarSize
  className?: string
  /** Wrap the icon in a tooltip showing the company name (on by default). */
  tooltip?: boolean
}

// Darker, more saturated versions of PASTEL_COLORS (same hues,
// same order, in UserAvatar) for the icon background.
const AVATAR_COLORS = [
  '#DB2777', // pink
  '#2563EB', // blue
  '#16A34A', // green
  '#D97706', // orange
  '#9333EA', // purple
  '#0D9488', // mint/teal
]

/**
 * The square-ish company icon (icon image or a coloured initial). It never
 * renders the company name — where a name is wanted, render it alongside, or
 * use CompanyLogo.
 */
const CompanyAvatar: React.FC<Props> = ({
  company,
  size = 'lg',
  className,
  tooltip = true,
}) => {
  /**
   * Picks a colour, using the presentation name as a seed, ensuring the same
   * company always gets the same colour.
   */
  function getColour(): string {
    let seedNumber = 0

    company.presentationName.split('').forEach((char) => {
      seedNumber += char.charCodeAt(0)
    })

    return AVATAR_COLORS[seedNumber % AVATAR_COLORS.length] as string
  }

  const avatar = (
    <Avatar size={size} className={cn('bg-white', className)}>
      {company.iconUrl && (
        <AvatarImage
          src={company.iconUrl}
          alt={`${company.presentationName}'s icon`}
        />
      )}

      <AvatarFallback
        className="rounded-lg font-bold uppercase"
        style={{
          backgroundColor: getColour(),
          // A lighter version of the BG colour
          color: `lch(from ${getColour()} 90 c h)`,
        }}
      >
        {company.presentationName.charAt(0)}
      </AvatarFallback>
    </Avatar>
  )

  if (!tooltip) {
    return avatar
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>{avatar}</TooltipTrigger>
      <TooltipContent>{company.presentationName}</TooltipContent>
    </Tooltip>
  )
}

export default CompanyAvatar
